import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  TextField,
  Select,
  MenuItem,
  Button,
  Typography,
  InputLabel,
  FormControl,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

// Subcomponentes para mejor organización
import DropboxOptions from './DropboxOptions';
import RepeatableSectionFields from './RepeatableSectionFields';
import { sanitizeIdForDom } from './formDomUtils';

const SortableInput = ({
  id,
  input,
  index,
  selectedInput,
  handleInputClick,
  handleTitleChange,
  handleInputUpdate,
  handleRequiredChange,
  handleSizeChange,
  handleAddOption,
  handleRemoveOption,
  handleOptionChange,
  handleRemoveInput,
  handleAddConditionalField,
  handleRemoveConditionalField,
  handleConditionalFieldChange,
  handleAddConditionalDropboxOption,
  handleRemoveConditionalDropboxOption,
  handleConditionalDropboxOptionChange,
  handleAddNestedConditionalField,
  handleRemoveNestedConditionalField,
  handleNestedConditionalFieldChange,
  handleAddNestedConditionalDropboxOption,
  handleRemoveNestedConditionalDropboxOption,
  handleNestedConditionalDropboxOptionChange,
  handleAddSectionField,
  handleRemoveSectionField,
  handleSectionFieldChange,
  handleAddSectionFieldDropboxOption,
  handleRemoveSectionFieldDropboxOption,
  handleSectionFieldDropboxOptionChange,
  handleAddSectionConditionalField,
  handleRemoveSectionConditionalField,
  handleSectionConditionalFieldChange,
  handleAddSectionConditionalDropboxOption,
  handleRemoveSectionConditionalDropboxOption,
  handleSectionConditionalDropboxOptionChange,
  handleConditionalRepeatableSectionFieldAdd,
  handleConditionalRepeatableSectionFieldRemove,
  handleConditionalRepeatableSectionFieldChange,
  handleConditionalRepeatableSectionDropboxOptionAdd,
  handleConditionalRepeatableSectionDropboxOptionRemove,
  handleConditionalRepeatableSectionDropboxOptionChange,
  handleConditionalRepeatableSectionUpdate,
  errors
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const domId = sanitizeIdForDom(id);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={`sortable-input ${isDragging ? 'dragging' : ''}`}
      style={{
        ...style,
        border: selectedInput === id ? '2px solid rgba(14, 119, 189, 1)' : '1px solid #ddd',
        padding: '10px',
        margin: '10px 0',
        borderRadius: '8px',
        backgroundColor: isDragging ? '#f5f5f5' : 'white',
        cursor: 'grab',
        position: 'relative',
      }}
      onClick={(e) => {
        if (!e.target.closest('.drag-handle')) {
          handleInputClick(id);
        }
      }}
    >
      {/* Header con drag handle */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '10px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <DragIndicatorIcon
            className="drag-handle"
            style={{
              cursor: 'grab',
              marginRight: '10px',
              color: '#666'
            }}
          />
          <Typography variant="h6" className="campo-title">
            CAMPO {index + 1}
          </Typography>
        </div>
      </div>

      {/* Nombre y tipo de campo */}
      <div className='form-input-row'>
        <TextField
          label="Nombre del campo"
          type="text"
          className='input-name'
          value={input.title || ''}
          onChange={(e) => handleTitleChange(id, e.target.value)}
          onClick={(e) => e.stopPropagation()}
        />
        <FormControl className='input-type' sx={{ marginLeft: 2 }}>
          <InputLabel htmlFor={`type-${domId}`}>Tipo</InputLabel>
          <Select
            label="Tipo"
            id={`type-${domId}`}
            value={input.type}
            onChange={(e) => handleInputUpdate(id, { type: e.target.value })}
            onClick={(e) => e.stopPropagation()}
          >
            <MenuItem value="TEXT">Texto</MenuItem>
            <MenuItem value="EMAIL">Correo electrónico</MenuItem>
            <MenuItem value="DATE">Fecha</MenuItem>
            <MenuItem value="URL">URL</MenuItem>
            <MenuItem value="DATE_TIME">Fecha y hora</MenuItem>
            <MenuItem value="NUMBER">Número</MenuItem>
            <MenuItem value="UPLOAD_FILE">Adjuntar archivos</MenuItem>
            <MenuItem value="UPLOAD">Fotografía</MenuItem>
            <MenuItem value="DROPBOX">Opción (dropbox)</MenuItem>
            <MenuItem value="MULTI_DROPBOX">Opciones múltiples</MenuItem>
            <MenuItem value="SIGNATURE">Firma</MenuItem>
            <MenuItem value="REPEATABLE_SECTION">Sección repetible</MenuItem>
            <MenuItem value="GEOLOCATION">Geolocalización (GPS)</MenuItem>
            <MenuItem value="AUTONUMBER">Autonumeración</MenuItem>
          </Select>
        </FormControl>
      </div>

      {/* Campo requerido */}
      <FormControlLabel
        control={
          <Checkbox
            checked={input.required !== false}
            onChange={(e) => handleRequiredChange(id, e.target.checked)}
            name="requiredCheckbox"
            onClick={(e) => e.stopPropagation()}
          />
        }
        label="Campo requerido"
      />

      {/* Opciones para TEXT */}
      {input.type === 'TEXT' && (
        <div>
          <FormControlLabel
            control={
              <Checkbox
                checked={input.size === "LARGE"}
                onChange={(e) => handleSizeChange(id, e.target.checked, 'LARGE')}
                name="sizeLargeCheckbox"
                onClick={(e) => e.stopPropagation()}
              />
            }
            label="Tamaño grande"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={input.size === "SHORT"}
                onChange={(e) => handleSizeChange(id, e.target.checked, 'SHORT')}
                name="sizeShortCheckbox"
                onClick={(e) => e.stopPropagation()}
              />
            }
            label="Tamaño corto"
          />
        </div>
      )}

      {/* Opciones para DROPBOX */}
      {input.type === 'DROPBOX' && (
        <DropboxOptions
          id={id}
          input={input}
          handleOptionChange={handleOptionChange}
          handleRemoveOption={handleRemoveOption}
          handleAddOption={handleAddOption}
          handleAddConditionalField={handleAddConditionalField}
          handleRemoveConditionalField={handleRemoveConditionalField}
          handleConditionalFieldChange={handleConditionalFieldChange}
          handleAddConditionalDropboxOption={handleAddConditionalDropboxOption}
          handleRemoveConditionalDropboxOption={handleRemoveConditionalDropboxOption}
          handleConditionalDropboxOptionChange={handleConditionalDropboxOptionChange}
          handleAddNestedConditionalField={handleAddNestedConditionalField}
          handleRemoveNestedConditionalField={handleRemoveNestedConditionalField}
          handleNestedConditionalFieldChange={handleNestedConditionalFieldChange}
          handleAddNestedConditionalDropboxOption={handleAddNestedConditionalDropboxOption}
          handleRemoveNestedConditionalDropboxOption={handleRemoveNestedConditionalDropboxOption}
          handleNestedConditionalDropboxOptionChange={handleNestedConditionalDropboxOptionChange}
          handleConditionalRepeatableSectionFieldAdd={handleConditionalRepeatableSectionFieldAdd}
          handleConditionalRepeatableSectionFieldRemove={handleConditionalRepeatableSectionFieldRemove}
          handleConditionalRepeatableSectionFieldChange={handleConditionalRepeatableSectionFieldChange}
          handleConditionalRepeatableSectionDropboxOptionAdd={handleConditionalRepeatableSectionDropboxOptionAdd}
          handleConditionalRepeatableSectionDropboxOptionRemove={handleConditionalRepeatableSectionDropboxOptionRemove}
          handleConditionalRepeatableSectionDropboxOptionChange={handleConditionalRepeatableSectionDropboxOptionChange}
          handleConditionalRepeatableSectionUpdate={handleConditionalRepeatableSectionUpdate}
        />
      )}

      {/* Opciones para MULTI_DROPBOX */}
      {input.type === 'MULTI_DROPBOX' && (
        <DropboxOptions
          id={id}
          input={input}
          handleOptionChange={handleOptionChange}
          handleRemoveOption={handleRemoveOption}
          handleAddOption={handleAddOption}
          handleAddConditionalField={handleAddConditionalField}
          handleRemoveConditionalField={handleRemoveConditionalField}
          handleConditionalFieldChange={handleConditionalFieldChange}
          handleAddConditionalDropboxOption={handleAddConditionalDropboxOption}
          handleRemoveConditionalDropboxOption={handleRemoveConditionalDropboxOption}
          handleConditionalDropboxOptionChange={handleConditionalDropboxOptionChange}
          handleAddNestedConditionalField={handleAddNestedConditionalField}
          handleRemoveNestedConditionalField={handleRemoveNestedConditionalField}
          handleNestedConditionalFieldChange={handleNestedConditionalFieldChange}
          handleAddNestedConditionalDropboxOption={handleAddNestedConditionalDropboxOption}
          handleRemoveNestedConditionalDropboxOption={handleRemoveNestedConditionalDropboxOption}
          handleNestedConditionalDropboxOptionChange={handleNestedConditionalDropboxOptionChange}
          handleConditionalRepeatableSectionFieldAdd={handleConditionalRepeatableSectionFieldAdd}
          handleConditionalRepeatableSectionFieldRemove={handleConditionalRepeatableSectionFieldRemove}
          handleConditionalRepeatableSectionFieldChange={handleConditionalRepeatableSectionFieldChange}
          handleConditionalRepeatableSectionDropboxOptionAdd={handleConditionalRepeatableSectionDropboxOptionAdd}
          handleConditionalRepeatableSectionDropboxOptionRemove={handleConditionalRepeatableSectionDropboxOptionRemove}
          handleConditionalRepeatableSectionDropboxOptionChange={handleConditionalRepeatableSectionDropboxOptionChange}
          handleConditionalRepeatableSectionUpdate={handleConditionalRepeatableSectionUpdate}
        />
      )}

      {/* Opciones para REPEATABLE_SECTION */}
      {input.type === 'REPEATABLE_SECTION' && (
        <RepeatableSectionFields
          id={id}
          input={input}
          handleInputUpdate={handleInputUpdate}
          handleAddSectionField={handleAddSectionField}
          handleRemoveSectionField={handleRemoveSectionField}
          handleSectionFieldChange={handleSectionFieldChange}
          handleAddSectionFieldDropboxOption={handleAddSectionFieldDropboxOption}
          handleRemoveSectionFieldDropboxOption={handleRemoveSectionFieldDropboxOption}
          handleSectionFieldDropboxOptionChange={handleSectionFieldDropboxOptionChange}
          handleAddSectionConditionalField={handleAddSectionConditionalField}
          handleRemoveSectionConditionalField={handleRemoveSectionConditionalField}
          handleSectionConditionalFieldChange={handleSectionConditionalFieldChange}
          handleAddSectionConditionalDropboxOption={handleAddSectionConditionalDropboxOption}
          handleRemoveSectionConditionalDropboxOption={handleRemoveSectionConditionalDropboxOption}
          handleSectionConditionalDropboxOptionChange={handleSectionConditionalDropboxOptionChange}
        />
      )}

      {/* Info para GEOLOCATION */}
      {input.type === 'GEOLOCATION' && (
        <div style={{ 
          backgroundColor: '#e3f2fd', 
          padding: '12px', 
          borderRadius: '8px', 
          marginTop: '10px',
          border: '1px solid #90caf9'
        }}>
          <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: '5px' }}>
            📍 Campo de Geolocalización
          </Typography>
          <Typography variant="body2" color="textSecondary">
            La app móvil capturará automáticamente las coordenadas GPS del dispositivo al rellenar este campo.
            Se guardará: latitud, longitud, precisión y fecha/hora.
          </Typography>
        </div>
      )}

      {/* Info para AUTONUMBER */}
      {input.type === 'AUTONUMBER' && (
        <div style={{ 
          backgroundColor: '#fff3e0', 
          padding: '12px', 
          borderRadius: '8px', 
          marginTop: '10px',
          border: '1px solid #ffcc80'
        }}>
          <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: '5px' }}>
            🔢 Campo de Autonumeración
          </Typography>
          <Typography variant="body2" color="textSecondary">
            La app móvil generará automáticamente un número único secuencial para cada documento.
            El usuario no podrá editarlo. Formato: YYYY-XXXXX (ej: 2026-00001)
          </Typography>
        </div>
      )}

      {/* Errores */}
      {errors[`${id}_title`] && <Typography color="error">{errors[`${id}_title`]}</Typography>}
      {errors[`${id}_name`] && <Typography color="error">{errors[`${id}_name`]}</Typography>}
      {errors[`${id}_options`] && <Typography color="error">{errors[`${id}_options`]}</Typography>}

      {/* Botón eliminar */}
      <Button
        type="button"
        className="remove-input-button"
        onClick={(e) => {
          e.stopPropagation();
          handleRemoveInput(id);
        }}
        style={{ marginTop: '10px' }}
      >
        <DeleteIcon color="inherit" />
        Eliminar
      </Button>
    </div>
  );
};

export default SortableInput;
